import HomePageV2 from '@/app/modules/HomePageV2';
import CMSContainer from '@/modules/CMSContainer';
import { faqSeoSchemaData } from '@/modules/HomePageV2/staticData/faqData';
import { DynamicPageData, DynamicPageType } from '@/types/cms';
import { buildCanonicalUrl } from '@/utils/canonicalUrl';
import { JOB_COUNT_PLACEHOLDER, formatJobCount } from '@/utils/jobCountCopy';
import { getLiveJobCount } from '@/utils/liveJobCount';
import { useAnalytics } from '@/utils/useAnalytics';
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import { FAQPageJsonLd, NextSeo } from 'next-seo';
import React, { useEffect } from 'react';
import { getDynamicPageData } from 'utils/getDynamicPageData';

type HomePageProps = DynamicPageData & {
  // Live job total, rounded and formatted for display - null if the job service was unavailable.
  jobCount?: string | null;
};

// The title and the H1 must quote the same number, so a CMS meta_title can only mention the job
// count through JOB_COUNT_PLACEHOLDER. Without a count to fill it in, fall back to copy that
// doesn't quote a number at all.
const getHomePageTitle = (metaTitle: string | null | undefined, jobCount?: string | null) => {
  const defaultTitle = jobCount
    ? `Search over ${jobCount} Doctor Jobs in Australia and New Zealand.`
    : 'Search Doctor Jobs in Australia and New Zealand.';

  if (!metaTitle) {
    return defaultTitle;
  }
  if (metaTitle.includes(JOB_COUNT_PLACEHOLDER)) {
    return jobCount ? metaTitle.split(JOB_COUNT_PLACEHOLDER).join(jobCount) : defaultTitle;
  }
  return metaTitle;
};

const HomePage = ({ pageData, jobCount }: HomePageProps) => {
  const title = getHomePageTitle(pageData.meta_title, jobCount);
  const defaultDescription = 'Discover the right doctor job for you in Australia or New Zealand';
  const track = useAnalytics();

  useEffect(() => {
    track('Page View', {
      'Page Title': 'Homepage',
      'Live experiments': [],
    });
  }, [track]);

  return (
    <CMSContainer shouldUpdateFooterStyles>
      <NextSeo
        canonical={pageData.canonical_url ? buildCanonicalUrl(pageData.canonical_url) : undefined}
        title={title}
        description={pageData.meta_description ?? defaultDescription}
        noindex={!pageData.index}
        nofollow={!pageData.index}
        openGraph={{
          title,
          description: pageData.meta_description ?? defaultDescription,
        }}
      />
      <FAQPageJsonLd mainEntity={faqSeoSchemaData} />
      <HomePageV2 jobCount={jobCount} />
    </CMSContainer>
  );
};

export async function getServerSideProps(
  context: GetServerSidePropsContext,
): Promise<GetServerSidePropsResult<HomePageProps>> {
  const [{ modules = [], pageData, error }, liveJobCount] = await Promise.all([
    getDynamicPageData(DynamicPageType.Home_Page, context, undefined),
    getLiveJobCount(context),
  ]);

  if (error) {
    return { notFound: true };
  }

  if (pageData?.redirect_url) {
    return {
      redirect: {
        destination: pageData.redirect_url,
        permanent: true,
      },
    };
  }

  return {
    props: {
      data: modules,
      pageData,
      jobCount: liveJobCount === null ? null : formatJobCount(liveJobCount),
    },
  };
}

export default HomePage;
